import { PaletteColorOptions, Theme } from '@mui/material/styles';

// ----------------------------------------------------------------------

declare module '@mui/material/styles' {
  interface CustomPalette {
    carbon: PaletteColorOptions;
    ccgrey: PaletteColorOptions;
  }
  interface Palette extends CustomPalette {}
  interface PaletteOptions extends CustomPalette {}
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    carbon: true;
    ccgrey: true;
  }
}

export default function Button(theme: Theme) {
  return {
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: 'none',
          fontSize: 16,
          padding: theme.spacing(1.2, 3),
          fontWeight: 400,
          '&:hover': {
            fontWeight: 700,
            '& .MuiButton-endIcon': {
              transform: 'rotateY(180deg)',
              transition: 'all .4s ease-in-out',
            },
          },
        },
        sizeSmall: {
          fontSize: 14,
          padding: theme.spacing(1, 1.5),
        },
        sizeLarge: {
          fontSize: 16,
          padding: theme.spacing(1, 5),
          [theme.breakpoints.up('lg')]: {
            padding: theme.spacing(1.3, 7),
          },
        },
        // contained
        containedInherit: {
          color: theme.palette.grey[800],
          boxShadow: theme.customShadows.z8,
          '&:hover': {
            backgroundColor: theme.palette.grey[400],
          },
        },
        contained: {
          '&.brandcube': {
            fontSize: 16,
          },
          '&:disabled': {
            '& .primary, .secondary': {
              fill: '#999594CC',
            },
            background: theme.palette.divider,
            '&.brandcube': {
              background: `conic-gradient(from calc(100deg/-2 - 40deg) at top 8px left 8px, #0000 90deg, ${theme.palette.divider} 0)`,
            }
          }
        },
        containedPrimary: {
          color: 'white',
          '&.brandcube': {
            background: `conic-gradient(from calc(100deg/-2 - 40deg) at top 8px left 8px, #0000 90deg, ${theme.palette.primary.main} 0)`,
          },
          '&:hover': {
            '&:not(.brandcube)': {
              background: theme.palette.primary.main,
            }
          }
        },
        containedSecondary: {
          color: theme.palette.branded.carbon,
          '&.brandcube': {
            background: `conic-gradient(from calc(100deg/-2 - 40deg) at top 8px left 8px, #0000 90deg, ${theme.palette.secondary.main} 0)`,
          },
          '&:hover': {
            '&:not(.brandcube)': {
              background: theme.palette.secondary.main,
            }
          },
          '& .primary': {
            fill: theme.palette.branded.carbon,
          },
          '& .secondary': {
            fill: theme.palette.primary.main,
          },
        },
        containedCarbon: {
          color: theme.palette.secondary.main,
          '&.brandcube': {
            background: `conic-gradient(from calc(100deg/-2 - 40deg) at top 8px left 8px, #0000 90deg, ${theme.palette.branded.carbon} 0)`,
          },
          '& .primary, .secondary': {
            fill: theme.palette.secondary.main,
          },
        },
        containedCcgrey: {
          color: '#fff',
          '&.brandcube': {
            background: `conic-gradient(from calc(100deg/-2 - 40deg) at top 8px left 8px, #0000 90deg, ${theme.palette.branded.grey} 0)`,
          },
          '& .primary, .secondary': {
            fill: '#fff',
          },
        },
        text: {
          fontWeight: 700,
          '&:hover': {
            background: 'transparent',
          },
          '&:disabled': {
            '& .primary, .secondary': {
              fill: '#999594CC',
            },
          },
          '&:has(svg)': {
            padding: 0,
          }
        },
        textSecondary: {
          color: theme.palette.secondary.main,
          '& .primary, .secondary': {
            fill: theme.palette.secondary.main,
          },
        },
        textCarbon: {
          '& .primary': {
            fill: theme.palette.branded.carbon,
          },
          '& .secondary': {
            fill: theme.palette.primary.main,
          },
        },
        // outlined
        outlined: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '&:disabled': {
            '& .primary, .secondary': {
              fill: '#999594CC',
            },
            border: `1px solid ${theme.palette.divider}`,
          }
        },
        outlinedPrimary: {
          border: `1px solid ${theme.palette.primary.main}`,
          '& .primary, .secondary': {
            fill: theme.palette.primary.main,
          }
        },
        outlinedSecondary: {
          border: `1px solid ${theme.palette.branded.carbon}`,
          color: theme.palette.branded.carbon,
          '& .primary': {
            fill: theme.palette.branded.carbon,
          },
          '& .secondary': {
            fill: theme.palette.primary.main,
          },
          '&:hover': {
            border: `1px solid ${theme.palette.branded.carbon}`,
          },
        },
        outlinedCarbon: {
          border: `1px solid ${theme.palette.branded.carbon}`,
          '& .primary': {
            fill: theme.palette.branded.carbon,
          },
          '& .secondary': {
            fill: theme.palette.primary.main,
          },
        },
        outlinedCcgrey: {
          border: `1px solid ${theme.palette.branded.grey}`,
        },
        outlinedInherit: {
          border: `1px solid ${theme.palette.grey[500_32]}`,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        },
        textInherit: {
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        },
      },
    },
  };
}
